import { FC, FormEvent, useCallback, useMemo, useState } from 'react'
import { Link as RouterLink, useLoaderData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@oasisprotocol/ui-library/src/components/cards'
import { Button } from '@oasisprotocol/ui-library/src/components/ui/button'
import { Link } from '@oasisprotocol/ui-library/src/components/link'
import { PageLayout } from '../../components/PageLayout'
import { useRuntimeScope } from '../../hooks/useScopeParam'
import { AddressLoaderData, RouteUtils } from '../../utils/route-utils'
import { getSourcifyServerUrl } from '../../utils/sourcify'

const CHAIN_ID = String(import.meta.env.REACT_APP_EVM_CHAIN_ID || 186)

type VerifyResult = {
  ok: boolean
  message: string
  status?: string
}

export const VerifyContractPage: FC = () => {
  const { t } = useTranslation()
  const scope = useRuntimeScope()
  const { address } = useLoaderData() as AddressLoaderData

  const [files, setFiles] = useState<FileList | null>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<VerifyResult | null>(null)

  const tokenHref = useMemo(() => RouteUtils.getTokenRoute(scope, address), [address, scope])
  const accountHref = useMemo(() => RouteUtils.getAccountRoute(scope, address), [address, scope])

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      setResult(null)
      if (!files || files.length === 0) {
        setResult({ ok: false, message: t('contract.verification.verifyMissingFiles') })
        return
      }

      const body = new FormData()
      body.append('address', address)
      body.append('chain', CHAIN_ID)
      Array.from(files).forEach(file => body.append('files', file, file.name))

      setBusy(true)
      try {
        const response = await fetch(`${getSourcifyServerUrl()}/verify`, {
          method: 'POST',
          body,
        })
        const payload = await response.json().catch(() => ({}))
        if (!response.ok) {
          const err =
            (typeof payload?.error === 'string' && payload.error) ||
            (typeof payload?.message === 'string' && payload.message) ||
            `HTTP ${response.status}`
          setResult({ ok: false, message: err })
          return
        }
        const status = payload?.result?.[0]?.status || payload?.result?.[0]?.runtimeMatch || 'perfect'
        setResult({
          ok: true,
          status: String(status),
          message: t('contract.verification.verifySuccess'),
        })
      } catch (error) {
        setResult({
          ok: false,
          message: error instanceof Error ? error.message : String(error),
        })
      } finally {
        setBusy(false)
      }
    },
    [address, files, t],
  )

  return (
    <PageLayout>
      <Card variant="layout" className="max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle>{t('contract.verification.verifyPageTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-[#64748B]">{t('contract.verification.verifyPageIntro')}</p>

          <dl className="grid gap-2 text-sm">
            <div className="flex flex-wrap gap-2 justify-between border-t border-[#EEF1F5] pt-2">
              <dt className="text-[#64748B]">{t('common.smartContract')}</dt>
              <dd className="font-mono break-all">{address}</dd>
            </div>
            <div className="flex flex-wrap gap-2 justify-between border-t border-[#EEF1F5] pt-2">
              <dt className="text-[#64748B]">{t('layerPicker.chainId')}</dt>
              <dd>
                {CHAIN_ID} <span className="text-[#64748B]">(0x{Number(CHAIN_ID).toString(16)})</span>
              </dd>
            </div>
          </dl>

          <form className="space-y-4" onSubmit={onSubmit}>
            <div>
              <label className="block text-sm font-medium mb-2" htmlFor="verify-files">
                {t('contract.verification.verifyFilesLabel')}
              </label>
              <input
                id="verify-files"
                type="file"
                multiple
                accept=".json,.sol,.vy,.txt"
                className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-[#0B63CE] file:px-3 file:py-1.5 file:text-white"
                onChange={e => setFiles(e.target.files)}
              />
              <p className="mt-2 text-xs text-[#64748B]">{t('contract.verification.verifyFilesHint')}</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={busy}>
                {busy ? t('contract.verification.verifying') : t('contract.verification.verifySubmit')}
              </Button>
              <Button type="button" variant="outline" asChild>
                <RouterLink to={tokenHref}>{t('contract.verification.backToToken')}</RouterLink>
              </Button>
              <Button type="button" variant="ghost" asChild>
                <RouterLink to={accountHref}>{t('contract.verification.backToContract')}</RouterLink>
              </Button>
            </div>
          </form>

          {result && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${
                result.ok
                  ? 'border-green-200 bg-green-50 text-green-800'
                  : 'border-red-200 bg-red-50 text-red-800'
              }`}
            >
              <p>{result.message}</p>
              {result.ok && result.status && (
                <p className="mt-1">
                  {t('contract.verification.verifyMatchStatus', { status: result.status })}
                </p>
              )}
              {result.ok && (
                <p className="mt-2">
                  <Link asChild className="font-semibold underline">
                    <RouterLink to={tokenHref}>{t('contract.verification.backToToken')}</RouterLink>
                  </Link>
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </PageLayout>
  )
}
